import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import PropTypes from 'prop-types';
import envConfig from '../../envConfig';
import RaaAuthenticatedRemoteService from '../../utility/RaaAuthenticatedRemoteService';
import { sessionHash } from '../../utility/remoteServiceUtil';

const remoteService = RaaAuthenticatedRemoteService.globalInstance();

const selectSessionInformation = (state) => state.session.sessionInformation;
const selectSessionHash = (state) => state.session.sessionHash;
const selectIsAuthenticated = (state) => state.session.isAuthenticated;
const selectActiveSubscriptionCounts = (state) => state.session.activeSubscriptionCounts;
const selectAllUserMessages = (state) => state.session.userMessages;
const selectUnreadUserMessages = createSelector(
  selectAllUserMessages,
  (messages) => messages.filter((message) => message.timeViewed === null),
);
const selectSubscriptionSwapUrl = (state) => state.session.subscriptionSwapRedirectUrl;
const selectUserAssignmentPreferences = (state) => state.session.userAssignmentPreferences;

const fetchUserMessages = createAsyncThunk(
  'securedApi/session/fetchUserMessages',
  async (_, thunkAPI) => {
    try {
      const userMessagesResponse = await remoteService.get(`${envConfig.apiUrl}/api/v1/account-messages`);
      return userMessagesResponse.unreadMessages
        ? userMessagesResponse.unreadMessages.sort((a, b) => b.timeCreated - a.timeCreated)
        : [];
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const fetchSessionData = createAsyncThunk(
  'securedApi/session/fetchSessionData',
  async (arg, thunkAPI) => {
    try {
      const results = await Promise.allSettled([
        remoteService.get(`${envConfig.apiUrl}/api/v1/session`),
        remoteService.get(`${envConfig.apiUrl}/api/v1/session/subscription/stats`),
        remoteService.get(`${envConfig.apiUrl}/api/v1/session/terms-accepted`),
        remoteService.get(`${envConfig.apiUrl}/api/v1/user-assignment-preference/`),
      ]);

      const hasRejectedPromises = results.some((result) => result.status === 'rejected');
      if (hasRejectedPromises) {
        return thunkAPI.rejectWithValue({
          name: 'RejectedPromiseError',
          message: 'One or more promises were rejected',
          data: null,
        });
      }

      return results.map((result) => result.value);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const initialState = {
  loading: true,
  error: false,
  isAuthenticated: false,
  sessionInformation: {
    temperatureScale: 'F', timezone: 'UTC', isSidebarChecklistHidden: false, airSpeedScale: 'm/s',
  },
  userAssignmentPreferences: { primaryChecklistHidden: true },
  sessionHash,
  refreshCallbacks: [],
  clearContextCallbacks: [],
  activeSubscriptionCounts: {
    userCount: 0,
    deviceCount: 0,
    alertCount: 0,
    reportCount: 0,
    connectorCount: 0,
    computedChannelCount: 0,
    legacyDeviceCount: 0,
    transferredDeviceCount: 0,
    archivedDeviceCount: 0,
    reportLimit: 0,
    computedChannelLimit: 0,
    connectorLimit: 0,
    userLimit: 0,
    deviceLimit: 0,
    alertLimit: 0,
    dataAgeLimitUnit: 'day',
    dataAgeLimitValue: 0,
  },
  userMessages: [],
  subscriptionSwapRedirectUrl: '',
};

const dismissUserMessage = createAsyncThunk(
  'securedApi/session/dismissUserMessage',
  async (messageId, thunkAPI) => {
    try {
      await RaaAuthenticatedRemoteService.globalInstance().post(
        `${envConfig.apiUrl}/api/v1/account-messages/dismiss-message`,
        { data: { messageId } },
      );
      return messageId;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const setHideChecklistOnMainNav = createAsyncThunk(
  'securedApi/subscriptionChecklist/setHideOnMainNav',
  async ({ hidden }, thunkAPI) => {
    try {
      const response = await RaaAuthenticatedRemoteService.globalInstance().post(
        `${envConfig.apiUrl}/api/v1/user-assignment-preference/checklist-hidden/${hidden}`,
      );
      return {
        hidden,
        success: response.success,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        hidden,
        error: {
          name: error.name,
          message: error.message,
          data: error.data,
        },
      });
    }
  },
);

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setSubscriptionSwapRedirectUrl: (state, action) => {
      state.subscriptionSwapRedirectUrl = action.payload;
    },
    clearSessionSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessionData.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(fetchSessionData.fulfilled, (state, action) => {
        const [
          sessionInfoResponse,
          countsResponse,
          termsAcceptedResponse,
          assignmentPreferenceResponse,
        ] = action.payload;
        state.loading = false;
        state.sessionInformation = {
          ...sessionInfoResponse.session,
          supportsRoles: !!sessionInfoResponse.session.supportsRoles,
        };
        state.activeSubscriptionCounts = countsResponse;
        state.isAuthenticated = true;
        if (!termsAcceptedResponse.termsAccepted) {
          const currentPath = window.location.pathname;
          window.location.assign(`${envConfig.raaUrl}/terms/acknowledge?referrer=${currentPath.substring(1)}`);
        }
        state.userAssignmentPreferences = {
          ...assignmentPreferenceResponse.preference,
        };
      })
      .addCase(fetchSessionData.rejected, (state) => {
        state.error = true;
        state.isAuthenticated = false;
        const currentPath = window.location.pathname;
        window.location.assign(`${envConfig.raaUrl}/gateway?referrer=${currentPath.substring(1)}`);
      })
      .addCase(fetchUserMessages.fulfilled, (state, action) => {
        state.userMessages = action.payload;
      })
      .addCase(dismissUserMessage.fulfilled, (state, action) => {
        state.userMessages = state.userMessages.filter(
          (message) => message.id !== action.payload,
        );
      })
      .addCase(setHideChecklistOnMainNav.fulfilled, (state, action) => {
        state.userAssignmentPreferences = {
          ...state.userAssignmentPreferences,
          primaryChecklistHidden: action.payload.hidden,
        };
      });
  },
});

const subscriptionPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  activeTierName: PropTypes.string.isRequired,
  activeTierDeviceLimit: PropTypes.number.isRequired,
  activeTierUserLimit: PropTypes.number.isRequired,
  activeTierAlertLimit: PropTypes.number.isRequired,
  activeTierDataAgeLimitUnit: PropTypes.string.isRequired,
  activeTierDataAgeLimitValue: PropTypes.number.isRequired,
  activeTierReportLimit: PropTypes.number.isRequired,
  activeTierComputedChannelLimit: PropTypes.number.isRequired,
  activeTierHasCsvExport: PropTypes.bool.isRequired,
  activeTierHasPublicUrl: PropTypes.bool.isRequired,
  activeTierSupport: PropTypes.string.isRequired,
  activeTierHasAdvancedAlerting: PropTypes.bool.isRequired,
  activeTierHasCameraMonitoring: PropTypes.bool.isRequired,
  activeTierHasAdmPlugins: PropTypes.bool.isRequired,
  activeTierHasReportRanges: PropTypes.number.isRequired,
  activeTierConnectorLimit: PropTypes.number.isRequired,
  limitMap: PropTypes.shape({
    reportLimit: PropTypes.number.isRequired,
    computedChannelLimit: PropTypes.number.isRequired,
    connectorLimit: PropTypes.number.isRequired,
    userLimit: PropTypes.number.isRequired,
    deviceLimit: PropTypes.number.isRequired,
    alertLimit: PropTypes.number.isRequired,
  }),
  role: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
  }),
  supportsRoles: PropTypes.number.isRequired,
  samlEnabled: PropTypes.bool.isRequired,
});
const sessionInformationPropType = PropTypes.shape({
  userId: PropTypes.number.isRequired,
  email: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  temperatureScale: PropTypes.string.isRequired,
  activeSubscriptionId: PropTypes.number.isRequired,
  userAssignmentList: PropTypes.arrayOf(subscriptionPropType).isRequired,
  activeSubscription: subscriptionPropType.isRequired,
});

const { actions, reducer } = sessionSlice;
export const {
  setSubscriptionSwapRedirectUrl,
  clearSessionSlice,
} = actions;

export {
  selectSessionInformation,
  selectIsAuthenticated,
  selectActiveSubscriptionCounts,
  selectUnreadUserMessages,
  selectSessionHash,
  selectAllUserMessages,
  selectSubscriptionSwapUrl,
  fetchUserMessages,
  fetchSessionData,
  sessionInformationPropType,
  subscriptionPropType,
  dismissUserMessage,
  selectUserAssignmentPreferences,
  setHideChecklistOnMainNav,
};
export default reducer;
